/*
 * as.if.radio shared storefront core
 * 02.09.2026: Weiterhoeren ohne Tap (attemptResume) und SPA-Vertrag (asif:page).
 * Rebuilt for the iOS-first radio product. This file is the sole owner of
 * audio, playback state, metadata, schedule, history and Media Session.
 */
(() => {
  'use strict';

  if (window.AsIfRadio?.version === '2.4.0') return;

  const host = document.querySelector('[data-asif-radio-root]');
  const audio = document.getElementById('rspAudio');
  if (!host || !audio) return;

  const config = Object.freeze({
    stream: host.dataset.stream,
    hls: host.dataset.hlsStream || '',
    hlsLow: host.dataset.hlsLow || '',
    hlsHigh: host.dataset.hlsHigh || '',
    lowStream: host.dataset.lowStream || host.dataset.stream,
    nowPlaying: host.dataset.nowPlayingApi,
    schedule: host.dataset.scheduleApi,
    history: host.dataset.historyApi,
    fallbackArtwork: host.dataset.fallbackArtwork || '',
    // Radio bricht nicht ab: Wiederverbinden laeuft weiter, solange der Hoerer
    // hoeren will. Nur die Wartezeit waechst.
    retryMaxDelay: 30000,
    bufferingTimeout: 15000,
    // Kein Fortschritt trotz "spielt": der stille Tod einer Icecast-Verbindung.
    stallTimeout: 12000
  });
  const qualityKey = 'asif_radio_quality_v1';
  let quality = 'auto';
  try { const saved = localStorage.getItem(qualityKey); if (['auto', 'low', 'high'].includes(saved)) quality = saved; } catch {}
  let startRequestedAt = 0;
  let audioStartMs = null;
  const storageKey = 'asif_radio_intent_v2';
  const favoriteStorageKey = 'asif_radio_favorites_v1';
  const channelName = 'asif-radio-player-v2';
  const maxFavorites = 100;
  const instanceId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const validStates = new Set([
    'idle', 'connecting', 'playing', 'buffering', 'paused',
    'retrying', 'offline', 'error'
  ]);
  const allowedArtworkHosts = new Set([
    window.location.hostname,
    'cdn.shopify.com',
    'stream.as-if-records.com',
    'radio-api.as-if-records.com',
    'www.as-if-records.com'
  ]);
  const parseArtworkURL = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value), document.baseURI);
      const isLocalQA = url.protocol === 'http:' && ['localhost', '127.0.0.1', '::1'].includes(url.hostname);
      const isSecureProductionURL = url.protocol === 'https:' && url.port === '';
      if ((!isSecureProductionURL && !isLocalQA) || url.username || url.password || !allowedArtworkHosts.has(url.hostname)) return '';
      return url.href;
    } catch (error) {
      return '';
    }
  };
  const fallbackArtworkURL = parseArtworkURL(config.fallbackArtwork);
  const normalizeArtworkURL = (value) => parseArtworkURL(value) || fallbackArtworkURL;

  let externalMediaOwner = false;
  let operation = 0;
  let hasEverEngaged = false;
  let hasSource = false;
  let retryTimer = 0;
  let bufferingTimer = 0;
  let bufferingStartedAt = 0;
  let nowPlayingTimer = 0;
  let scheduleTimer = 0;
  let historyTimer = 0;
  let channel = null;
  let primaryObserver = null;
  let primaryFallbackCleanup = null;
  let scheduleRequested = false;
  let historyRequested = false;
  let playbackSession = 0;
  let playbackStartedAt = 0;
  let playbackAccumulatedMs = 0;
  let playbackReported = false;
  let terminalReported = false;
  let activeSurface = 'unknown';
  let activeClaim = null;
  let mediaSessionPlaybackState = '';
  let mediaSessionMetadataSignature = '';
  let documentSuspended = false;
  let airPlayAvailabilityListening = false;
  let sourceHasPlayed = false;
  let needsLiveRejoin = false;
  let lastProgressAt = 0;
  let lastPlaybackTime = 0;
  let interrupted = false;
  let hls = null;
  let transport = 'mp3';
  let hlsFailures = 0;
  let stablePlaybackSince = 0;
  let stallTimer = 0;
  let snapshotCache = null;
  let renderQueued = false;
  let domDirty = false;
  let audioSessionSet = false;
  let hasPlayedOnce = false;
  let hlsReturnAt = 0;
  let unsupportedCount = 0;
  const emittedAnalytics = new Set();

  const state = {
    quality,
    trackStartedAt: 0,
    trackEndsAt: 0,
    status: 'idle',
    playing: false,
    loading: false,
    wantsToPlay: false,
    needsResume: false,
    error: false,
    errorCode: '',
    retryCount: 0,
    title: 'as.if.radio',
    artist: 'as.if.records',
    album: '',
    trackId: '',
    program: 'as.if.radio',
    artworkUrl: fallbackArtworkURL,
    currentFavorite: false,
    favorites: [],
    airPlayAvailable: false,
    airPlayActive: false,
    metadataStatus: 'loading',
    metadataUpdatedAt: '',
    scheduleStatus: 'idle',
    historyStatus: 'idle',
    schedule: [],
    history: []
  };

  const requests = {
    nowPlaying: { controller: null, requestId: 0, promise: null, etag: '', updatedAt: 0, failureCount: 0 },
    schedule: { controller: null, requestId: 0, promise: null, etag: '', updatedAt: 0, failureCount: 0 },
    history: { controller: null, requestId: 0, promise: null, etag: '', updatedAt: 0, failureCount: 0 }
  };

  const copy = {
    idle: { button: 'Radio an', status: 'Bereit.' },
    connecting: { button: 'Verbindet...', status: 'Stream wird gestartet.' },
    playing: { button: 'Pause', status: 'Live.' },
    buffering: { button: 'Pause', status: 'Verbindung wird stabilisiert.' },
    paused: { button: 'Weiterhören', status: 'Pausiert.' },
    retrying: { button: 'Abbrechen', status: 'Verbindung wird erneut aufgebaut.' },
    offline: { button: 'Abbrechen', status: 'Offline. Wir warten auf eine Verbindung.' },
    error: { button: 'Erneut versuchen', status: 'Radio gerade nicht erreichbar.' }
  };

  // Die tiefe Kopie ist teuer und wurde bei jedem Hover ueber einen Link erzeugt.
  // Sie wird jetzt zwischengespeichert und erst nach einer Aenderung neu gebaut.
  const snapshot = () => {
    if (!snapshotCache) {
      snapshotCache = {
        ...state,
        schedule: state.schedule.slice(),
        history: state.history.slice(),
        favorites: state.favorites.map((item) => ({ ...item }))
      };
    }
    return snapshotCache;
  };

  const persistIntent = () => {
    try {
      sessionStorage.setItem(storageKey, state.wantsToPlay ? '1' : '0');
    } catch (error) {}
  };

  const readPersistedIntent = () => {
    try {
      return sessionStorage.getItem(storageKey) === '1';
    } catch (error) {
      return false;
    }
  };

  const analyticsAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
    try {
      return privacy.analyticsProcessingAllowed() === true;
    } catch (error) {
      return false;
    }
  };

  const publishAnalytics = (eventName, data, eventKey) => {
    const publish = window.Shopify?.analytics?.publish;
    const dedupeKey = `${playbackSession}:${eventKey}`;
    if (!analyticsAllowed() || typeof publish !== 'function' || emittedAnalytics.has(dedupeKey)) return false;
    emittedAnalytics.add(dedupeKey);
    try {
      Promise.resolve(publish.call(window.Shopify.analytics, `asif_radio:${eventName}`, {
        surface: activeSurface,
        context: document.body.classList.contains('asif-radio-product') ? 'radio-page' : 'storefront',
        ...data
      })).catch(() => emittedAnalytics.delete(dedupeKey));
      return true;
    } catch (error) {
      emittedAnalytics.delete(dedupeKey);
      return false;
    }
  };

  const startPlaybackSegment = () => {
    if (playbackReported && !playbackStartedAt) playbackStartedAt = Date.now();
  };

  const stopPlaybackSegment = () => {
    if (!playbackStartedAt) return;
    playbackAccumulatedMs += Math.max(0, Date.now() - playbackStartedAt);
    playbackStartedAt = 0;
  };

  const playedSeconds = () => Math.max(0, Math.round(playbackAccumulatedMs / 1000));

  const compareClaims = (first, second) => {
    const timeDifference = Number(first?.at || 0) - Number(second?.at || 0);
    if (timeDifference !== 0) return timeDifference;
    return String(first?.id || '').localeCompare(String(second?.id || ''));
  };

  const broadcastClaim = () => {
    if (!channel || !activeClaim) return;
    channel.postMessage({ type: 'claim', source: instanceId, claim: activeClaim });
  };

  const surfaceForControl = (control) => {
    if (control?.closest('[data-asif-radio-primary]')) return 'radio-primary';
    if (control?.closest('[data-asif-radio-dock]')) return 'radio-dock';
    if (control?.closest('[data-asif-home-radio]')) return 'home-radio';
    if (control?.closest('[data-hero-radio]')) return 'brand-hero';
    return 'storefront-control';
  };

  const setText = (selector, value) => {
    document.querySelectorAll(selector).forEach((node) => {
      if (node.textContent !== value) node.textContent = value;
      node.classList.toggle('asif-wordmark', /^(as\.if\.radio|as\.if\.records)$/.test(value));
    });
  };

  // Blindes setAttribute schmutzt den Baum und laesst den Screenreader neu rechnen.
  const setAttr = (node, name, value) => {
    if (node.getAttribute(name) !== value) node.setAttribute(name, value);
  };

  const selectAndCopyText = (value) => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const field = document.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', '');
    field.setAttribute('aria-hidden', 'true');
    field.style.position = 'fixed';
    field.style.inset = '0 auto auto -9999px';
    field.style.opacity = '0';
    document.body.append(field);
    field.select();
    field.setSelectionRange(0, field.value.length);
    let copied = false;
    try {
      copied = document.execCommand('copy') === true;
    } catch (error) {}
    field.remove();
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    return copied;
  };

  const copyStreamAddress = () => {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(config.stream).then(
        () => true,
        () => selectAndCopyText(config.stream)
      );
    }
    return Promise.resolve(selectAndCopyText(config.stream));
  };

  const paintRecordArtwork = (image) => {
    const canvas = image.parentElement?.querySelector('[data-asif-radio-art-canvas]');
    if (!(canvas instanceof HTMLCanvasElement) || !image.complete || image.naturalWidth < 1 || image.naturalHeight < 1) return;
    const source = image.currentSrc || image.src;
    if (!source || canvas.dataset.asifRadioArtworkSource === source) return;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return;
    const sourceRatio = image.naturalWidth / image.naturalHeight;
    const sourceSize = sourceRatio > 1 ? image.naturalHeight : image.naturalWidth;
    const sourceX = sourceRatio > 1 ? (image.naturalWidth - sourceSize) / 2 : 0;
    const sourceY = sourceRatio < 1 ? (image.naturalHeight - sourceSize) / 2 : 0;
    context.fillStyle = '#0f1111';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, canvas.width, canvas.height);
    canvas.dataset.asifRadioArtworkSource = source;
  };

  const renderArtwork = () => {
    document.querySelectorAll('[data-asif-radio-art]').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      if (image.closest('[data-asif-radio-dock]')?.classList.contains('is-primary-visible')) return;
      if (image.hasAttribute('data-asif-radio-art-canvas-source') && image.dataset.asifRadioCanvasBound !== 'true') {
        image.dataset.asifRadioCanvasBound = 'true';
        image.addEventListener('load', () => paintRecordArtwork(image));
      }
      const usesStableFallback = image.dataset.asifRadioArtMode === 'fallback';
      const nextSource = usesStableFallback
        ? fallbackArtworkURL
        : normalizeArtworkURL(state.artworkUrl);
      if (!nextSource) return;
      if (image.src !== nextSource) image.src = nextSource;
      image.alt = usesStableFallback || state.title === 'as.if.radio'
        ? 'as.if.radio'
        : `${state.artist} - ${state.title}`;
      paintRecordArtwork(image);
    });
  };

  const buttonLabel = () => interrupted && state.wantsToPlay ? copy.retrying.button
    : state.needsResume ? copy.paused.button : (copy[state.status]?.button || copy.idle.button);
  const statusLabel = () => {
    if (state.status === 'playing' && state.metadataStatus === 'unavailable') {
      return 'Live. Trackinfo gerade nicht verfügbar.';
    }
    return copy[state.status]?.status || copy.idle.status;
  };

  const syncMediaSession = () => {
    if (externalMediaOwner || !('mediaSession' in navigator)) return;
    try {
      // Waehrend Verbinden, Puffern und Wiederverbinden bleibt die Systemtaste auf
      // "spielt": sonst flackert sie auf dem Sperrbildschirm bei jedem Aussetzer.
      const busy = ['connecting', 'buffering', 'retrying', 'offline'].includes(state.status);
      const nextPlaybackState = state.status === 'playing' || (busy && state.wantsToPlay)
        ? 'playing'
        : (state.status === 'idle' ? 'none' : 'paused');
      if (nextPlaybackState !== mediaSessionPlaybackState) {
        navigator.mediaSession.playbackState = nextPlaybackState;
        mediaSessionPlaybackState = nextPlaybackState;
      }
      const metadataSignature = [state.title, state.artist, state.program, state.artworkUrl].join('\u001f');
      if ('MediaMetadata' in window && metadataSignature !== mediaSessionMetadataSignature) {
        // iOS nimmt fuer die kleine Ansicht im Kontrollzentrum eine andere Groesse
        // als fuer den Sperrbildschirm. Dieselbe Quelle mehrfach anbieten.
        const artworkSource = state.artworkUrl || fallbackArtworkURL;
        const artwork = artworkSource
          ? ['96x96', '128x128', '256x256', '512x512'].map((sizes) => ({ src: artworkSource, sizes }))
          : [];
        navigator.mediaSession.metadata = new MediaMetadata({
          title: state.title || 'as.if.radio',
          artist: state.artist || 'as.if.records',
          album: state.program || 'as.if.radio',
          artwork
        });
        mediaSessionMetadataSignature = metadataSignature;
      }
    } catch (error) {}
  };

  const emit = () => {
    window.dispatchEvent(new CustomEvent('asif:radio-state', { detail: snapshot() }));
  };

  // Ein Durchlauf laeuft zwoelfmal durch das Dokument. Er wird deshalb gebuendelt:
  // im Vordergrund auf das naechste Bild, im Hintergrund auf den naechsten Tick.
  // MediaSession bleibt synchron, sonst haengt der Sperrbildschirm hinterher.
  const render = () => {
    state.playing = state.status === 'playing';
    state.loading = ['connecting', 'buffering', 'retrying'].includes(state.status);
    state.error = state.status === 'error';
    snapshotCache = null;
    syncMediaSession();
    if (renderQueued) return;
    renderQueued = true;
    const run = () => {
      renderQueued = false;
      paint();
      emit();
    };
    if (document.hidden) window.setTimeout(run, 0);
    else window.requestAnimationFrame(run);
  };

  const paint = () => {
    const label = buttonLabel();
    const statusCopy = statusLabel();

    host.dataset.state = state.status;
    host.dataset.transport = transport;
    host.dataset.errorCode = state.errorCode;
    document.documentElement.dataset.asifRadioState = state.status;
    document.querySelectorAll('[data-asif-radio-toggle]').forEach((button) => {
      setAttr(button, 'aria-pressed', state.playing ? 'true' : 'false');
      if (button.dataset.radioState !== state.status) button.dataset.radioState = state.status;
      const labelNode = button.querySelector('[data-asif-radio-label]');
      if (labelNode) labelNode.textContent = label;
      else button.setAttribute('aria-label', label);
    });
    setText('[data-asif-radio-title]', state.title);
    setText('[data-asif-radio-artist]', state.artist);
    setText('[data-asif-radio-program]', state.program);
    setText('[data-asif-radio-status]', statusCopy);
    setText('[data-asif-radio-stream-address]', config.stream);
    document.querySelectorAll('[data-asif-radio-favorite-current]').forEach((button) => {
      // Runde 6: die Taste fuer den laufenden Titel traegt dessen Kennung,
      // sonst findet der Klick-Handler oben keinen Titel zum Speichern.
      if (button.dataset.asifRadioTrackId !== (state.trackId || '')) button.dataset.asifRadioTrackId = state.trackId || '';
      if (button.disabled !== !state.trackId) button.disabled = !state.trackId;
      setAttr(button, 'aria-pressed', state.currentFavorite ? 'true' : 'false');
      setAttr(button, 'aria-label', state.currentFavorite ? 'Aus gespeicherten Titeln entfernen' : 'Titel speichern');
      const label = button.querySelector('[data-asif-radio-favorite-label]');
      if (label) label.textContent = state.currentFavorite ? 'Gespeichert' : 'Speichern';
    });
    const favoriteIds = new Set(state.favorites.map((item) => item.id));
    document.querySelectorAll('[data-asif-radio-track-id]').forEach((button) => {
      const isFavorite = favoriteIds.has(button.dataset.asifRadioTrackId);
      setAttr(button, 'aria-pressed', isFavorite ? 'true' : 'false');
      setAttr(button, 'aria-label', isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen');
      const hint = isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen';
      if (button.title !== hint) button.title = hint;
      const label = button.querySelector('[data-asif-radio-favorite-label]');
      if (label) label.textContent = isFavorite ? 'Gespeichert' : 'Speichern';
    });
    document.querySelectorAll('[data-asif-radio-favorite-count]').forEach((node) => {
      node.textContent = String(state.favorites.length + (window.AsIfRadioCollection?.getEntries().length || 0));
    });
    document.querySelectorAll('[data-asif-radio-airplay]').forEach((button) => {
      if (button.hidden === state.airPlayAvailable) button.hidden = !state.airPlayAvailable;
      if (button.disabled === state.airPlayAvailable) button.disabled = !state.airPlayAvailable;
      setAttr(button, 'aria-pressed', state.airPlayActive ? 'true' : 'false');
    });
    renderArtwork();
  };

  const transition = (nextStatus, patch = {}) => {
    if (!validStates.has(nextStatus)) return;
    const changed = state.status !== nextStatus
      || Object.keys(patch).some((key) => state[key] !== patch[key]);
    Object.assign(state, patch, { status: nextStatus });
    if (changed) render();
  };

  const clearRetry = ({ resetCount = false } = {}) => {
    window.clearTimeout(retryTimer);
    retryTimer = 0;
    if (resetCount) state.retryCount = 0;
  };

  const clearBufferingWatchdog = () => {
    window.clearTimeout(bufferingTimer);
    bufferingTimer = 0;
  };

  const abortDataRequests = () => {
    Object.values(requests).forEach((slot) => {
      slot.requestId += 1;
      slot.controller?.abort();
      slot.controller = null;
      slot.promise = null;
    });
  };

  // Die Verbindung wird nur noch abgerissen, wenn sie ohnehin tot ist. Frueher
  // passierte das bei jeder Pause und bei jedem Wechsel in den Hintergrund: auf
  // iOS verschwindet damit der Eintrag im Sperrbildschirm, und Weiterhoeren
  // kostete einen kompletten Neuaufbau.
  const releaseSource = () => {
    hasSource = false;
    if (hls) { const previous = hls; hls = null; previous.destroy(); }
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  };

  // Pause ohne Abriss: das Element behaelt Quelle und Systemsteuerung.
  const holdSource = () => {
    if (!audio.paused) audio.pause();
    hls?.stopLoad();
  };

  // Native MP3 buffering is browser-owned. Never seek to buffered.end(): that
  // throws away the only cushion against the next mobile network gap.
  const bufferAhead = () => {
    try {
      for (let i = 0; i < audio.buffered.length; i += 1) {
        if (audio.buffered.start(i) <= audio.currentTime && audio.buffered.end(i) > audio.currentTime) {
          return audio.buffered.end(i) - audio.currentTime;
        }
      }
    } catch (error) {}
    return 0;
  };
  const isAdvancing = () => {
    const time = audio.currentTime;
    if (!audio.paused && time > lastPlaybackTime + 0.01) {
      lastPlaybackTime = time;
      lastProgressAt = Date.now();
      if (stablePlaybackSince && Date.now() - stablePlaybackSince >= 30000) {
        hlsFailures = 0;
        // Audit 24.09.: nach einem Ausfall zurueck vom MP3-Notweg auf HLS
        // (Stufenwahl, bessere Qualitaet), hoechstens alle 10 Minuten.
        if (transport === 'mp3' && config.hls && !document.hidden && Date.now() >= hlsReturnAt
          && (nativeHls || window.Hls?.isSupported?.())) {
          hlsReturnAt = Date.now() + 10 * 60000;
          needsLiveRejoin = true;
        }
      }
      return true;
    }
    return false;
  };

  // Prefer native HLS (including AirPlay); load the pinned light adapter only
  // where needed. It makes no stream request until the listener presses Play.
  const canPlayNativeHls = Boolean(config.hls && audio.canPlayType?.('application/vnd.apple.mpegurl'));
  // Safari's native pipeline preserves OS/AirPlay integration. Prefer MSE on
  // other capable engines: native canPlayType alone did not prove reliable HLS
  // decoding in the browser QA. Both paths retain the MP3 fallback.
  const nativeHls = canPlayNativeHls && (/Apple/.test(navigator.vendor || '') || !window.MediaSource);
  // 23.09.2026: hls.js (rund 120 KB) nicht mehr auf jeder Shopseite laden.
  // Sofort nur auf Radio- und Startseite oder bei bestehender Hoerabsicht,
  // sonst beim ersten Kontakt mit einem Radio-Knopf. Kommt Play vor dem
  // Laden, spielt der vorhandene MP3-Weg; spaetere Verbindungen nutzen HLS.
  let hlsLibraryRequested = false;
  const loadHlsLibrary = () => {
    if (hlsLibraryRequested || window.Hls || nativeHls || !config.hls || !host.dataset.hlsLibrary) return;
    hlsLibraryRequested = true;
    const script = document.createElement('script');
    script.src = host.dataset.hlsLibrary;
    script.async = true;
    document.head.appendChild(script);
  };
  const hlsEagerTemplate = () => /^(index|page\.asif-radio)$/.test(document.body?.dataset?.asifTemplate || '');
  if (hlsEagerTemplate() || readPersistedIntent()) loadHlsLibrary();
  else if (config.hls && !nativeHls && host.dataset.hlsLibrary && !window.Hls) {
    const warmHls = (event) => {
      if (!event.target?.closest?.('[data-asif-radio-toggle], [data-hub-open], .hub')) return;
      loadHlsLibrary();
      ['pointerover', 'pointerdown', 'focusin'].forEach((type) => document.removeEventListener(type, warmHls, true));
    };
    ['pointerover', 'pointerdown', 'focusin'].forEach((type) => document.addEventListener(type, warmHls, { capture: true, passive: true }));
    document.addEventListener('asif:page', () => { if (hlsEagerTemplate()) loadHlsLibrary(); });
  }
  // 24.09.2026 (Pierre): Welche Qualitaet laeuft gerade wirklich? low, mid, high
  // oder null (unbekannt). Bei Automatisch waehlt hls.js die Stufe selbst und
  // meldet sie; Safari entscheidet intern und verraet sie nicht.
  let effectiveLevel = null;
  const levelFromBitrate = (bps) => (!bps ? null : bps <= 64000 ? 'low' : bps <= 128000 ? 'mid' : 'high');
  const setEffectiveLevel = (value) => {
    if (value === effectiveLevel) return;
    effectiveLevel = value;
    emit();
  };
  // --- Stufenwahl bei Automatisch, 24.09.2026 (Pierre) -----------------------
  // Ziel: immer die beste Stufe, die die Leitung sicher traegt. Reicht das Netz,
  // von Anfang an high, auch auf dem iPhone. Bei schwachem Netz niedrig starten
  // und hochschalten, sobald es reicht; eine kurze Tonluecke beim Wechsel ist
  // in Safari in Ordnung. Stockt es, eine Stufe runter.
  // Safari (iPhone, iPad, Mac, installierte App) spielt HLS selbst und verraet
  // seine Wahl nicht, deshalb waehlt der Player dort selbst eine feste Stufe und
  // misst die Leitung mit dem neuesten Stueck der hohen Stufe (rund 138 KB).
  // hls.js (Chrome, Android, Firefox, Edge) wechselt selbst und nahtlos; es
  // bekommt nur die gemerkte Leitung als Startwert, damit es gleich hoch startet.
  const LEVELS = ['low', 'mid', 'high'];
  const LEVEL_KBPS = { low: 53, mid: 106, high: 211 };
  const HEADROOM = 2.2;
  const bandwidthKey = 'asif_radio_bandwidth_v1';
  const BANDWIDTH_TTL = 20 * 60000;
  const variantURL = (level) => {
    if (level === 'low') return config.hlsLow;
    if (level === 'high') return config.hlsHigh;
    return config.hlsHigh ? config.hlsHigh.replace('aac_hifi', 'aac_midfi') : '';
  };
  const levelForKbps = (kbps) => (kbps >= LEVEL_KBPS.high * HEADROOM ? 'high' : kbps >= LEVEL_KBPS.mid * HEADROOM ? 'mid' : 'low');
  const readBandwidth = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(bandwidthKey) || 'null');
      if (saved && Date.now() - saved.at < BANDWIDTH_TTL && saved.kbps > 0) return saved.kbps;
    } catch (error) {}
    return 0;
  };
  const saveBandwidth = (kbps) => { try { localStorage.setItem(bandwidthKey, JSON.stringify({ kbps: Math.round(kbps), at: Date.now() })); } catch (error) {} };
  // Wieder online nach einem Funkloch: alte Messung gilt nicht mehr. (Nicht bei
  // jedem Android-Netzwechsel-Ereignis, die kommen zu oft.)
  const forgetBandwidth = () => { try { localStorage.removeItem(bandwidthKey); } catch (error) {} };
  window.addEventListener('online', forgetBandwidth);

  let nativeLevel = levelForKbps(readBandwidth());
  let nativeProbeTimer = 0;
  let nativeProbe = null;
  let nativeProbeBackoff = 60000;
  let nativeHoldUntil = 0;
  let nativeStepDowns = 0;
  let nativeLastStepDown = 0;
  const nativeWaits = [];
  const nativeAbrActive = () => quality === 'auto' && transport === 'hls-native' && navigator.connection?.saveData !== true && !!(config.hlsLow && config.hlsHigh);
  const probeBandwidth = () => {
    if (nativeProbe) return nativeProbe;
    nativeProbe = (async () => {
      const base = config.hlsHigh;
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 12000);
      try {
        const list = await fetch(`${base}${base.includes('?') ? '&' : '?'}_=${Date.now()}`, { cache: 'no-store', credentials: 'omit', signal: controller.signal });
        const lines = (await list.text()).split(/\r?\n/).map((line) => line.trim()).filter((line) => line && !line.startsWith('#'));
        const last = lines[lines.length - 1];
        if (!last) return 0;
        const started = performance.now();
        const response = await fetch(new URL(last, base).href, { cache: 'no-store', credentials: 'omit', signal: controller.signal });
        const bytes = (await response.arrayBuffer()).byteLength;
        const ms = Math.max(1, performance.now() - started);
        const kbps = bytes > 20000 ? (bytes * 8) / ms : 0;
        // Waehrend der Sperrzeit nach einem Stocker nicht ueberschreiben: sonst
        // startet ein Neuladen gleich wieder oben.
        if (kbps > 0 && Date.now() >= nativeHoldUntil) saveBandwidth(kbps);
        return kbps;
      } catch (error) {
        // Abgebrochen nach 12 s: die Leitung traegt nicht einmal 92 kbit/s.
        return controller.signal.aborted ? 1 : 0;
      } finally {
        window.clearTimeout(timer);
        nativeProbe = null;
      }
    })();
    return nativeProbe;
  };
  // Vorab messen, sobald jemand einen Radio-Knopf beruehrt: dann steht die
  // Stufe oft schon fest, bevor der Ton startet.
  if (nativeHls && config.hlsLow && config.hlsHigh) {
    document.addEventListener('pointerdown', (event) => {
      if (quality !== 'auto' || readBandwidth() || document.hidden) return;
      if (!event.target?.closest?.('[data-asif-radio-toggle], [data-hub-open], .hub, .stage')) return;
      probeBandwidth();
    }, { capture: true, passive: true });
  }
  const switchNativeLevel = (level, reason) => {
    if (level === nativeLevel) return;
    nativeLevel = level;
    // Im Hintergrund (gesperrt) nicht selbst neu verbinden: die neue Stufe gilt
    // beim naechsten Verbindungsaufbau, den der Player ohnehin selbst macht.
    if (!state.wantsToPlay || externalMediaOwner || interrupted || document.hidden) return;
    needsLiveRejoin = true;
    clearRetry();
    scheduleRetry(reason, true);
  };
  const upgradeFromProbe = async () => {
    const kbps = await probeBandwidth();
    if (!nativeAbrActive() || !state.wantsToPlay) return;
    const target = kbps > 0 ? levelForKbps(kbps) : nativeLevel;
    if (LEVELS.indexOf(target) > LEVELS.indexOf(nativeLevel) && Date.now() >= nativeHoldUntil) {
      nativeProbeBackoff = 60000;
      switchNativeLevel(target, 'quality-change');
    } else {
      // Reicht nicht: seltener messen (1, 2, 4, hoechstens 5 min), damit die
      // Messung eine schwache Leitung nicht zusaetzlich belastet.
      nativeProbeBackoff = Math.min(nativeProbeBackoff * 2, 300000);
    }
  };
  const scheduleNativeProbe = (delay) => {
    window.clearTimeout(nativeProbeTimer);
    nativeProbeTimer = window.setTimeout(async () => {
      nativeProbeTimer = 0;
      if (!nativeAbrActive() || nativeLevel === 'high') return;
      if (state.status === 'playing' && !document.hidden) await upgradeFromProbe();
      if (nativeAbrActive() && nativeLevel !== 'high') scheduleNativeProbe(nativeProbeBackoff);
    }, delay);
  };
  // Start ohne frische Messung: sofort messen, parallel zum Verbindungsaufbau.
  // Kommt das Ergebnis, bevor Ton laeuft, wechselt der Player ohne hoerbare
  // Luecke gleich auf die bessere Stufe.
  const startNativeProbe = () => {
    if (!nativeAbrActive() || nativeLevel === 'high' || document.hidden) return;
    upgradeFromProbe().finally(() => {
      if (nativeAbrActive() && nativeLevel !== 'high') scheduleNativeProbe(nativeProbeBackoff);
    });
  };
  audio.addEventListener('playing', () => {
    if (!nativeAbrActive() || nativeLevel === 'high' || nativeProbeTimer || nativeProbe) return;
    scheduleNativeProbe(nativeLevel === 'low' ? 5000 : 15000);
  });
  audio.addEventListener('waiting', () => {
    if (!nativeAbrActive() || !sourceHasPlayed || nativeLevel === 'low') return;
    const now = Date.now();
    nativeWaits.push(now);
    while (nativeWaits.length && now - nativeWaits[0] > 90000) nativeWaits.shift();
    const from = nativeLevel;
    const stepDown = () => {
      if (nativeLevel !== from) return;
      const lower = LEVELS[LEVELS.indexOf(from) - 1];
      nativeWaits.length = 0;
      // Wiederholtes Stocken: laenger unten bleiben (2, 4, 8, hoechstens 15 min),
      // damit es nicht im Minutentakt hoch und runter geht.
      if (Date.now() - nativeLastStepDown > 30 * 60000) nativeStepDowns = 0;
      nativeStepDowns += 1;
      nativeLastStepDown = Date.now();
      nativeHoldUntil = Date.now() + Math.min(2 * 2 ** (nativeStepDowns - 1), 15) * 60000;
      nativeProbeBackoff = 60000;
      saveBandwidth(LEVEL_KBPS[lower] * HEADROOM);
      switchNativeLevel(lower, 'quality-change');
    };
    if (nativeWaits.length >= 2) { stepDown(); return; }
    // Haengt ein einzelnes Nachladen laenger als 5 s, ebenfalls eine Stufe runter.
    const waitOperation = operation;
    window.setTimeout(() => {
      if (waitOperation === operation && nativeAbrActive() && state.status === 'buffering') stepDown();
    }, 5000);
  });
  // hls.js: Startstufe aus der gemerkten Leitung, danach entscheidet hls.js.
  const hlsStartLevel = (levels) => {
    const kbps = readBandwidth();
    if (!kbps || !levels?.length) return 0;
    let pick = 0;
    levels.forEach((level, index) => { if ((level.bitrate || 0) / 1000 * HEADROOM <= kbps) pick = index; });
    return pick;
  };

  const streamURL = (url, fresh) => {
    if (!fresh) return url;
    return `${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}`;
  };
  const connectSource = (fresh) => {
    sourceHasPlayed = false;
    needsLiveRejoin = false;
    if (hls) { const previous = hls; hls = null; previous.destroy(); }
    const constrained = navigator.connection?.saveData || ['slow-2g', '2g', '3g'].includes(navigator.connection?.effectiveType);
    const preferLow = quality === 'low' || (quality === 'auto' && constrained);
    // Automatisch nutzt bei HLS immer die Stufenwahl (Pierre 24.09.): ein als
    // langsam geschaetztes Netz (2g, 3g) begrenzt die Stufe nicht mehr, die
    // Messung entscheidet. Nur ein bewusst eingeschalteter Datensparmodus des
    // Geraets haelt Automatisch auf low. Der MP3-Rueckfall kann nicht wechseln
    // und bleibt deshalb vorsichtig.
    const capLow = quality === 'low' || (quality === 'auto' && navigator.connection?.saveData === true);
    const hlsURL = (capLow && config.hlsLow) || (quality === 'high' && config.hlsHigh) || config.hls;
    const adaptive = hlsURL && hlsFailures < 2;
    hasSource = true;
    const urlLevel = hlsURL === config.hlsLow ? 'low' : hlsURL === config.hlsHigh ? 'high' : null;
    if (adaptive && nativeHls) {
      transport = 'hls-native';
      // Automatisch in Safari: eigene Stufenwahl statt Master-Playlist (auch bei
      // Datensparmodus: dann startet die Messung ohnehin niedrig).
      const own = !capLow && quality === 'auto' && config.hlsLow && config.hlsHigh;
      const level = own ? nativeLevel : urlLevel;
      setEffectiveLevel(level);
      audio.setAttribute('src', streamURL(own ? variantURL(level) : hlsURL, fresh));
      if (own && !readBandwidth()) startNativeProbe();
    } else if (adaptive && window.Hls?.isSupported()) {
      transport = 'hls-mse';
      const engine = new window.Hls({
        // ~32s server playlist window. Trade some live latency for mobile reserve.
        liveSyncDurationCount: 5,
        maxBufferLength: 24,
        maxMaxBufferLength: 30,
        backBufferLength: 8,
        lowLatencyMode: false,
        startLevel: 0,
        autoStartLoad: false,
        // Gemerkte Leitung als Startschaetzung, sonst die hls.js-Vorgabe.
        ...(readBandwidth() ? { abrEwmaDefaultEstimate: readBandwidth() * 1000 } : {})
      });
      hls = engine;
      engine.on(window.Hls.Events.ERROR, (_event, data) => {
        if (hls !== engine || !state.wantsToPlay || !data.fatal) return;
        scheduleRetry('hls-fatal');
      });
      // Bei Automatisch die letzte Anzeige stehen lassen, bis hls.js meldet.
      if (urlLevel) setEffectiveLevel(urlLevel);
      engine.on(window.Hls.Events.MANIFEST_PARSED, (_event, data) => {
        if (hls !== engine || !state.wantsToPlay) return;
        if (!urlLevel) engine.startLevel = hlsStartLevel(data.levels || engine.levels);
        engine.startLoad(-1);
      });
      if (!urlLevel) {
        engine.on(window.Hls.Events.LEVEL_SWITCHED, (_event, data) => {
          if (hls !== engine) return;
          setEffectiveLevel(levelFromBitrate(engine.levels?.[data.level]?.bitrate));
        });
        // hls.js misst bei jedem Stueck mit; das Ergebnis merkt sich das Geraet
        // fuer den naechsten Start (alle 30 s, nur bei ungestoerter Wiedergabe).
        // Erst ab dem dritten Stueck: davor ist es noch die Vorgabe von hls.js.
        let savedAt = 0;
        let fragments = 0;
        engine.on(window.Hls.Events.FRAG_LOADED, () => {
          fragments += 1;
          if (hls !== engine || fragments < 3 || Date.now() - savedAt < 30000) return;
          const estimate = engine.bandwidthEstimate;
          if (estimate > 0 && Number.isFinite(estimate)) { savedAt = Date.now(); saveBandwidth(estimate / 1000); }
        });
      }
      engine.loadSource(hlsURL);
      engine.attachMedia(audio);
    } else {
      transport = 'mp3';
      const source = preferLow || hlsFailures >= 2 || state.retryCount >= 2 ? config.lowStream : config.stream;
      setEffectiveLevel(source === config.lowStream ? 'low' : 'high');
      audio.setAttribute('src', streamURL(source, fresh));
    }
  };

  // Feature-detected playback session; OS interruptions still take priority.
  const declareAudioSession = () => {
    if (audioSessionSet) return;
    try {
      if (navigator.audioSession) {
        navigator.audioSession.type = 'playback';
        audioSessionSet = true;
      }
    } catch (error) {}
  };

  const clearStallWatch = () => {
    window.clearInterval(stallTimer);
    stallTimer = 0;
  };

  // "Spielt" ohne Fortschritt ist der haeufigste stille Ausfall auf Mobilfunk.
  const startStallWatch = () => {
    clearStallWatch();
    lastProgressAt = Date.now();
    lastPlaybackTime = audio.currentTime;
    stallTimer = window.setInterval(() => {
      if (!state.wantsToPlay || interrupted || state.status !== 'playing') return;
      if (isAdvancing()) return;
      if (Date.now() - lastProgressAt < config.stallTimeout) return;
      clearStallWatch();
      scheduleRetry('stalled-silent');
    }, 4000);
  };

  const classifyPlayError = (error) => {
    if (error?.name === 'NotAllowedError') return 'not-allowed';
    if (error?.name === 'NotSupportedError') return 'not-supported';
    return 'network';
  };

  const terminalError = (code) => {
    operation += 1;
    clearRetry({ resetCount: false });
    clearBufferingWatchdog();
    clearStallWatch();
    stopPlaybackSegment();
    state.wantsToPlay = false;
    persistIntent();
    releaseSource();
    // 23.09.2026: Blockiert der Browser nur das automatische Weiterhoeren nach
    // einem Neuladen, ist das kein Fehler. Stille Pause mit "Weiterhoeren"
    // statt "Radio gerade nicht erreichbar".
    const blockedResume = code === 'not-allowed' && activeSurface === 'resume';
    transition(blockedResume ? 'paused' : 'error', {
      errorCode: blockedResume ? '' : code,
      needsResume: code === 'not-allowed'
    });
    if (!blockedResume && !terminalReported && playbackSession > 0) {
      publishAnalytics('terminal_error', {
        code,
        retries: state.retryCount
      }, 'terminal-error');
      terminalReported = true;
    }
    playbackStartedAt = 0;
    playbackAccumulatedMs = 0;
    playbackReported = false;
    activeClaim = null;
  };

  const startBufferingWatchdog = () => {
    if (bufferingTimer) return;
    bufferingStartedAt = Date.now();
    const expectedOperation = operation;
    bufferingTimer = window.setTimeout(() => {
      bufferingTimer = 0;
      if (expectedOperation !== operation || !state.wantsToPlay || interrupted) return;
      if (isAdvancing()) { startBufferingWatchdog(); return; }
      scheduleRetry('buffer-timeout');
    }, config.bufferingTimeout);
  };

  const beginPlay = ({ userGesture = false, retry = false, notify = true, surface = 'unknown' } = {}) => {
    if (userGesture && !retry) {
      // Audit 24.09.: Ein Tippen schlaegt eine haengengebliebene Unterbrechung
      // (Anruf, CarPlay, iOS meldet das Ende nicht immer) und alte Fehlerzaehler.
      interrupted = false;
      unsupportedCount = 0;
      externalMediaOwner = false;
      window.dispatchEvent(new CustomEvent('asif:audio-owner', { detail: { owner: 'live' } }));
      mediaSessionPlaybackState = '';
      mediaSessionMetadataSignature = '';
      bindLiveMediaSession();
    }
    if (externalMediaOwner || interrupted) return Promise.resolve(false);
    const currentOperation = ++operation;
    clearRetry();
    clearBufferingWatchdog();
    hasEverEngaged = true;
    state.wantsToPlay = true;
    state.needsResume = false;
    state.errorCode = '';
    persistIntent();

    if (userGesture && !retry) {
      startRequestedAt = Date.now();
      audioStartMs = null;
      hlsFailures = 0;
      playbackSession += 1;
      playbackStartedAt = 0;
      playbackAccumulatedMs = 0;
      playbackReported = false;
      terminalReported = false;
      activeSurface = surface;
      activeClaim = {
        at: Date.now(),
        id: instanceId
      };
    }

    declareAudioSession();

    // Laeuft schon: nur den Zustand geradeziehen. Ein zweites play() auf einem
    // spielenden Element loest kein playing-Ereignis aus, die Oberflaeche waere
    // sonst dauerhaft auf "Verbindet".
    if (!retry && state.status === 'playing' && !needsLiveRejoin && hasSource && !audio.paused && !audio.ended && !audio.error && audio.readyState > 2) {
      transition('playing', { errorCode: '', needsResume: false });
      return Promise.resolve(true);
    }

    transition(retry ? 'retrying' : 'connecting');

    try {
      // Live radio has no pause position: every actual re-entry loads the
      // current programme and discards the old decoder/segment buffer.
      connectSource(retry || hasSource);
      startBufferingWatchdog();
      const playPromise = audio.play();
      return Promise.resolve(playPromise).then(() => {
        if (currentOperation !== operation || !state.wantsToPlay) return false;
        return true;
      }).catch((error) => {
        if (currentOperation !== operation || !state.wantsToPlay) return false;
        const code = classifyPlayError(error);
        // NotAllowedError heisst: der Browser will eine Geste. Das ist endgueltig,
        // bis der Hoerer tippt. NotSupportedError meldet WebKit dagegen auch dann,
        // wenn die Verbindung einfach abgerissen ist - deshalb wird nur aufgegeben,
        // wenn nie etwas lief und es mehrfach hintereinander passiert.
        if (code === 'not-allowed') {
          terminalError(code);
        } else if (code === 'not-supported') {
          unsupportedCount += 1;
          if (!hasPlayedOnce && unsupportedCount >= 3) terminalError(code);
          else scheduleRetry(code);
        } else {
          scheduleRetry(code);
        }
        return false;
      });
    } catch (error) {
      const code = classifyPlayError(error);
      if (currentOperation !== operation) return Promise.resolve(false);
      if (code === 'not-allowed') terminalError(code);
      else scheduleRetry(code);
      return Promise.resolve(false);
    }
  };

  const pause = ({ notify = true, preserveResume = false, surface = activeSurface, reason = 'user' } = {}) => {
    const shouldReportPause = playbackReported;
    stopPlaybackSegment();
    const duration = playedSeconds();
    if (surface) activeSurface = surface;
    operation += 1;
    clearRetry({ resetCount: true });
    clearBufferingWatchdog();
    state.wantsToPlay = false;
    state.needsResume = preserveResume;
    state.errorCode = '';
    persistIntent();
    clearStallWatch();
    holdSource();
    transition(hasEverEngaged ? 'paused' : 'idle');
    if (shouldReportPause) {
      publishAnalytics('paused', { playedSeconds: duration, reason }, 'paused');
    }
    playbackStartedAt = 0;
    playbackAccumulatedMs = 0;
    playbackReported = false;
    activeClaim = null;
    if (notify && channel) channel.postMessage({ type: 'release', source: instanceId });
  };

  function scheduleRetry(reason, immediate = false) {
    if (!state.wantsToPlay || interrupted || externalMediaOwner || retryTimer) return;
    // load() verwirft ein noch offenes play()-Promise. Dessen spaete Antwort
    // darf weder diesen Retry noch einen neueren Start ueberschreiben.
    operation += 1;
    clearBufferingWatchdog();
    clearStallWatch();
    stopPlaybackSegment();
    if (hasSource && transport.startsWith('hls') && !['online', 'network-change', 'pageshow', 'resume-visible', 'interruption-ended', 'live-rejoin', 'quality-change'].includes(reason)) hlsFailures += 1;
    releaseSource();

    // Kein Abbruch mehr nach fuenf Versuchen: ein Tunnel dauert laenger als eine
    // halbe Minute. Es waechst nur die Wartezeit, gedeckelt bei 30 Sekunden.
    const attempt = state.retryCount + 1;
    const base = immediate ? 0 : Math.min(config.retryMaxDelay, 1000 * (2 ** Math.min(attempt - 1, 5)));
    const jitter = immediate ? 0 : Math.round(base * (Math.random() * 0.3 - 0.15));
    state.retryCount = attempt;
    transition(navigator.onLine === false ? 'offline' : 'retrying', { errorCode: reason || 'network' });
    const expectedOperation = operation;
    retryTimer = window.setTimeout(() => {
      retryTimer = 0;
      if (!state.wantsToPlay || expectedOperation !== operation) return;
      beginPlay({ retry: true, notify: false });
    }, Math.min(config.retryMaxDelay, Math.max(0, base + jitter)));
  }

  const toggleFromGesture = (control) => {
    const surface = surfaceForControl(control);
    if (state.wantsToPlay) {
      pause({ surface });
      return;
    }
    beginPlay({ userGesture: true, surface });
  };

  const normalizedText = (value, fallback, maxLength = 180) => {
    if (typeof value !== 'string') return fallback;
    const cleaned = value.trim().replace(/\s+/g, ' ');
    return cleaned ? cleaned.slice(0, maxLength) : fallback;
  };

  const normalizeTrackId = (value, artist = '', title = '') => {
    const explicitId = normalizedText(value, '', 180);
    if (explicitId) return explicitId;
    const signature = `${normalizedText(artist, '', 180)}\u001f${normalizedText(title, '', 180)}`.trim();
    return signature === '\u001f' ? '' : `track:${signature}`;
  };

  const normalizeFavorite = (item) => {
    if (!item || typeof item !== 'object') return null;
    const title = normalizedText(item.title, '', 180);
    const artist = normalizedText(item.artist, '', 180);
    const generatedId = normalizeTrackId('', artist, title);
    const id = item.id === generatedId ? generatedId : normalizeTrackId(item.id, artist, title);
    if (!id || !title || !artist || (title === 'as.if.radio' && artist === 'as.if.records')) return null;
    return {
      id,
      title,
      artist,
      album: normalizedText(item.album, '', 180),
      artworkUrl: normalizeArtworkURL(item.artworkUrl),
      savedAt: normalizedDate(item.savedAt) || new Date().toISOString()
    };
  };

  const readFavorites = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(favoriteStorageKey) || '[]');
      if (!Array.isArray(parsed)) return [];
      const unique = new Map();
      parsed.slice(0, maxFavorites).forEach((item) => {
        const favorite = normalizeFavorite(item);
        if (favorite && !unique.has(favorite.id)) unique.set(favorite.id, favorite);
      });
      return [...unique.values()];
    } catch (error) {
      return [];
    }
  };

  const persistFavorites = () => {
    try {
      localStorage.setItem(favoriteStorageKey, JSON.stringify(state.favorites.slice(0, maxFavorites)));
      return true;
    } catch (error) {
      return false;
    }
  };

  const currentTrack = () => normalizeFavorite({
    id: state.trackId,
    title: state.title,
    artist: state.artist,
    album: state.album,
    artworkUrl: state.artworkUrl,
    savedAt: new Date().toISOString()
  });

  const syncCurrentFavorite = () => {
    state.currentFavorite = Boolean(state.trackId && state.favorites.some((item) => item.id === state.trackId));
  };

  const toggleFavorite = (track) => {
    const favorite = normalizeFavorite(track);
    if (!favorite) return false;
    const existingIndex = state.favorites.findIndex((item) => item.id === favorite.id);
    if (existingIndex >= 0) state.favorites.splice(existingIndex, 1);
    else state.favorites.unshift(favorite);
    state.favorites = state.favorites.slice(0, maxFavorites);
    persistFavorites();
    syncCurrentFavorite();
    render();
    return existingIndex < 0;
  };

  const copyText = (value) => {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(value).then(
        () => true,
        () => selectAndCopyText(value)
      );
    }
    return Promise.resolve(selectAndCopyText(value));
  };

  const shareRadio = () => {
    const track = currentTrack();
    const url = new URL('/pages/radio', window.location.origin).href;
    const data = {
      title: 'as.if.radio',
      text: track ? `${track.artist}: ${track.title} auf as.if.radio` : 'as.if.radio live hören',
      url
    };
    if (typeof navigator.share === 'function') {
      return navigator.share(data).then(() => 'shared').catch((error) => error?.name === 'AbortError' ? 'cancelled' : copyText(url).then((copied) => copied ? 'copied' : 'failed'));
    }
    return copyText(url).then((copied) => copied ? 'copied' : 'failed');
  };

  const normalizedDate = (value) => {
    if (typeof value !== 'string' || value.length > 64) return '';
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : '';
  };

  const validateNowPlaying = (data) => {
    if (!data || typeof data !== 'object' || !data.track || typeof data.track !== 'object') {
      throw new Error('invalid-now-playing');
    }
    return {
      track: {
        id: normalizeTrackId(data.track.id, data.track.artist, data.track.title),
        title: normalizedText(data.track.title, 'as.if.radio'),
        artist: normalizedText(data.track.artist, 'as.if.records'),
        album: normalizedText(data.track.album, '', 180)
      },
      program: {
        title: normalizedText(data.program?.title, 'as.if.radio', 120)
      },
      artworkUrl: typeof data.artworkUrl === 'string' && data.artworkUrl.length <= 2048 ? data.artworkUrl : '',
      observedAt: normalizedDate(data.observedAt),
      ...(() => {
        const span = trackWindow(data);
        return span
          ? { startedAt: new Date(span.start).toISOString(), endsAt: new Date(span.end).toISOString() }
          : { startedAt: '', endsAt: '' };
      })(),
      stale: data.stale === true
    };
  };

  // 23.09.2026 Pierre: Titelfortschritt als gelbe Linie am Player. Start und
  // Ende kommen aus der Radio-API; fehlen sie, aus Laufzeit und Position.
  const trackWindow = (data) => {
    let start = Date.parse(data?.startedAt || '');
    let end = Date.parse(data?.endsAt || '');
    const progress = data?.trackProgress;
    if ((!Number.isFinite(start) || !Number.isFinite(end)) && progress) {
      const observed = Date.parse(progress.observedAt || data?.observedAt || '');
      const elapsed = Number(progress.elapsedSeconds);
      const duration = Number(progress.durationSeconds);
      if (Number.isFinite(observed) && Number.isFinite(elapsed) && Number.isFinite(duration) && duration > 0) {
        start = observed - elapsed * 1000;
        end = start + duration * 1000;
      }
    }
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > 3 * 60 * 60 * 1000) return null;
    return { start, end };
  };

  const validateCollection = (data, kind) => {
    if (!data || typeof data !== 'object' || !Array.isArray(data.items)) {
      throw new Error(`invalid-${kind}`);
    }
    if (kind === 'schedule') {
      return data.items.slice(0, 200).flatMap((item, index) => {
        if (!item || typeof item !== 'object') return [];
        const startsAt = normalizedDate(item.startsAt);
        const endsAt = normalizedDate(item.endsAt);
        if (!startsAt || !endsAt || Date.parse(endsAt) <= Date.parse(startsAt)) return [];
        const title = normalizedText(item.title, 'as.if.radio', 120);
        return [{
          id: normalizedText(item.id, `schedule:${startsAt}:${index}`, 180),
          programId: normalizedText(item.programId, '', 180),
          programHandle: normalizedText(item.programHandle, '', 120),
          title,
          startsAt,
          endsAt
        }];
      });
    }
    if (kind === 'history') {
      return data.items.slice(0, 50).flatMap((item, index) => {
        if (!item || typeof item !== 'object') return [];
        const playedAt = normalizedDate(item.playedAt);
        if (!playedAt) return [];
        return [{
          id: normalizedText(item.id, `history:${playedAt}:${index}`, 180),
          playedAt,
          durationSeconds: Number.isFinite(Number(item.durationSeconds)) && Number(item.durationSeconds) > 0
            ? Math.min(Number(item.durationSeconds), 24 * 60 * 60)
            : 0,
          playlistTitle: normalizedText(item.playlistTitle, '', 180),
          isRequest: item.isRequest === true,
          track: {
            id: normalizeTrackId(item.track?.id, item.track?.artist, item.track?.title),
            title: normalizedText(item.track?.title, 'Unbekannter Titel'),
            artist: normalizedText(item.track?.artist, 'as.if.radio'),
            album: normalizedText(item.track?.album, '', 180)
          },
          artworkUrl: normalizeArtworkURL(item.artworkUrl)
        }];
      });
    }
    return [];
  };

  const loadJSON = (kind, url, parse, { force = false, maxAge = 0 } = {}) => {
    const slot = requests[kind];
    if (slot.promise) return slot.promise;
    if (!force && slot.updatedAt && Date.now() - slot.updatedAt < maxAge) {
      return Promise.resolve(kind === 'nowPlaying'
        ? null
        : { data: state[kind], stale: false });
    }

    const requestId = ++slot.requestId;
    slot.controller?.abort();
    const controller = new AbortController();
    slot.controller = controller;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 8000);
    const headers = slot.etag ? { 'If-None-Match': slot.etag } : {};

    slot.promise = fetch(url, {
      mode: 'cors',
      cache: 'no-store',
      headers,
      signal: controller.signal
    }).then((response) => {
      if (response.status === 304) return null;
      if (!response.ok) throw new Error(`http-${response.status}`);
      slot.etag = response.headers.get('ETag') || slot.etag;
      return response.json().then((data) => ({
        data: parse(data),
        stale: response.headers.get('X-AsIf-Metadata-Stale') === 'true'
      }));
    }).then((result) => {
      if (requestId !== slot.requestId) return null;
      slot.updatedAt = Date.now();
      slot.failureCount = 0;
      return result;
    }).catch((error) => {
      if (requestId === slot.requestId && (error?.name !== 'AbortError' || timedOut)) {
        slot.failureCount = Math.min(5, slot.failureCount + 1);
      }
      if (timedOut && error?.name === 'AbortError') {
        const timeoutError = new Error(`${kind}-timeout`);
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      throw error;
    }).finally(() => {
      window.clearTimeout(timeout);
      if (requestId === slot.requestId) {
        slot.promise = null;
        slot.controller = null;
      }
    });
    return slot.promise;
  };

  const listSignature = (items, keys) => (items || [])
    .map((item) => keys.map((key) => String(item?.[key] ?? '')).join('\u001e'))
    .join('\u001f');

  const applyNowPlaying = (result) => {
    if (!result?.data) return;
    const before = [state.trackId, state.title, state.artist, state.album, state.program, state.artworkUrl, state.metadataStatus, state.trackStartedAt, state.trackEndsAt].join('\u001f');
    const data = result.data;
    const track = data.track || {};
    const program = data.program || {};
    state.trackId = track.id || '';
    state.title = track.title || 'as.if.radio';
    state.artist = track.artist || 'as.if.records';
    state.album = track.album || '';
    state.program = String(program.title || 'as.if.radio').replace(/_/g, ' ');
    state.artworkUrl = normalizeArtworkURL(data.artworkUrl);
    const observedTime = Date.parse(data.observedAt || '');
    const metadataAge = Number.isFinite(observedTime) ? Date.now() - observedTime : Number.POSITIVE_INFINITY;
    state.metadataStatus = data.stale || result.stale || metadataAge > 5 * 60 * 1000 ? 'stale' : 'current';
    state.metadataUpdatedAt = Number.isFinite(observedTime) ? new Date(observedTime).toISOString() : '';
    const span = trackWindow(data);
    state.trackStartedAt = span && state.metadataStatus === 'current' ? span.start : 0;
    state.trackEndsAt = span && state.metadataStatus === 'current' ? span.end : 0;
    syncCurrentFavorite();
    // Alle 15 Sekunden dieselbe Ausgabe neu zu zeichnen kostet auf jedem Geraet
    // Zeit und laesst die Oberflaeche zaeh wirken. Nur bei Aenderung zeichnen.
    const after = [state.trackId, state.title, state.artist, state.album, state.program, state.artworkUrl, state.metadataStatus, state.trackStartedAt, state.trackEndsAt].join('\u001f');
    if (after !== before) render();
  };

  const refreshNowPlaying = ({ force = false } = {}) => loadJSON(
    'nowPlaying',
    config.nowPlaying,
    validateNowPlaying,
    { force, maxAge: 10000 }
  ).then(applyNowPlaying).catch((error) => {
    if (error?.name === 'AbortError') return;
    state.metadataStatus = 'unavailable';
    render();
  });

  const buildScheduleURL = () => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    from.setDate(from.getDate() - 1);
    const to = new Date(from);
    to.setDate(to.getDate() + 9);
    const url = new URL(config.schedule);
    url.searchParams.set('from', from.toISOString());
    url.searchParams.set('to', to.toISOString());
    return url.href;
  };

  const refreshSchedule = ({ force = false } = {}) => {
    const wasStatus = state.scheduleStatus;
    state.scheduleStatus = state.schedule.length ? state.scheduleStatus : 'loading';
    if (state.scheduleStatus !== wasStatus) render();
    const before = listSignature(state.schedule, ['startsAt', 'endsAt', 'title']);
    return loadJSON(
      'schedule',
      buildScheduleURL(),
      (data) => validateCollection(data, 'schedule'),
      { force, maxAge: 5 * 60 * 1000 }
    ).then((result) => {
      if (result?.data) state.schedule = result.data;
      const status = state.schedule.length ? 'current' : 'empty';
      const changed = status !== state.scheduleStatus
        || listSignature(state.schedule, ['startsAt', 'endsAt', 'title']) !== before;
      state.scheduleStatus = status;
      if (changed) render();
      return state.schedule.slice();
    }).catch((error) => {
      if (error?.name !== 'AbortError') {
        state.scheduleStatus = state.schedule.length ? 'stale' : 'unavailable';
        render();
      }
      return state.schedule.slice();
    });
  };

  const refreshHistory = ({ force = false } = {}) => {
    const wasStatus = state.historyStatus;
    state.historyStatus = state.history.length ? state.historyStatus : 'loading';
    if (state.historyStatus !== wasStatus) render();
    const beforeHistory = listSignature(state.history, ['playedAt']);
    return loadJSON(
      'history',
      config.history,
      (data) => validateCollection(data, 'history'),
      { force, maxAge: 30000 }
    ).then((result) => {
      if (result?.data) state.history = result.data;
      const status = state.history.length ? 'current' : 'empty';
      const changed = status !== state.historyStatus
        || listSignature(state.history, ['playedAt']) !== beforeHistory;
      state.historyStatus = status;
      if (changed) render();
      return state.history.slice();
    }).catch((error) => {
      if (error?.name !== 'AbortError') {
        state.historyStatus = state.history.length ? 'stale' : 'unavailable';
        render();
      }
      return state.history.slice();
    });
  };

  const scheduleNowPlayingTimer = () => {
    window.clearTimeout(nowPlayingTimer);
    nowPlayingTimer = 0;
    if (documentSuspended) return;
    const baseDelay = document.hidden
      ? (state.wantsToPlay ? 45000 : 90000)
      : (state.wantsToPlay ? 15000 : 45000);
    let nowDelay = Math.min(15 * 60 * 1000, baseDelay * (2 ** Math.min(requests.nowPlaying.failureCount, 4)));
    // Titelwechsel nicht erst beim naechsten Takt: kurz nach dem Titelende nachfragen.
    const untilEnd = state.trackEndsAt ? state.trackEndsAt + 3000 - Date.now() : 0;
    if (!document.hidden && untilEnd > 1000 && untilEnd < nowDelay) nowDelay = untilEnd;
    nowPlayingTimer = window.setTimeout(() => {
      refreshNowPlaying({ force: true }).finally(scheduleNowPlayingTimer);
    }, nowDelay);
  };

  const scheduleScheduleTimer = () => {
    window.clearTimeout(scheduleTimer);
    scheduleTimer = 0;
    if (documentSuspended) return;
    if (scheduleRequested) {
      const delay = Math.min(30 * 60 * 1000, 5 * 60 * 1000 * (2 ** Math.min(requests.schedule.failureCount, 3)));
      scheduleTimer = window.setTimeout(() => {
        refreshSchedule({ force: true }).finally(scheduleScheduleTimer);
      }, delay);
    }
  };

  const scheduleHistoryTimer = () => {
    window.clearTimeout(historyTimer);
    historyTimer = 0;
    if (documentSuspended) return;
    if (historyRequested) {
      const baseDelay = state.wantsToPlay ? 30000 : 60000;
      const delay = Math.min(10 * 60 * 1000, baseDelay * (2 ** Math.min(requests.history.failureCount, 4)));
      historyTimer = window.setTimeout(() => {
        refreshHistory({ force: true }).finally(scheduleHistoryTimer);
      }, delay);
    }
  };

  const scheduleDataTimers = () => {
    scheduleNowPlayingTimer();
    scheduleScheduleTimer();
    scheduleHistoryTimer();
  };

  // 23.09.2026: Sendeplan und Verlauf nur abfragen, solange eine Seite sie
  // braucht. Beim Seitenwechsel zuruecksetzen; Bühne und Radioseite melden
  // ihren Bedarf beim Einbinden neu an.
  document.addEventListener('asif:page:start', () => {
    scheduleRequested = false;
    historyRequested = false;
    scheduleScheduleTimer();
    scheduleHistoryTimer();
  });

  const ensureData = ({ schedule = false, history = false, force = false } = {}) => {
    scheduleRequested = scheduleRequested || schedule;
    historyRequested = historyRequested || history;
    const work = [refreshNowPlaying({ force })];
    if (schedule) work.push(refreshSchedule({ force }));
    if (history) work.push(refreshHistory({ force }));
    scheduleDataTimers();
    return Promise.all(work);
  };

  const bindPrimaryVisibility = () => {
    primaryObserver?.disconnect();
    primaryObserver = null;
    primaryFallbackCleanup?.();
    primaryFallbackCleanup = null;
    const primary = document.querySelector('[data-asif-radio-primary], [data-asif-home-radio]');
    if (!primary) {
      host.classList.remove('is-primary-visible');
      host.removeAttribute('inert');
      host.setAttribute('aria-hidden', 'false');
      return;
    }

    const setPrimaryVisible = (visible) => {
      host.classList.toggle('is-primary-visible', visible);
      host.toggleAttribute('inert', visible);
      host.setAttribute('aria-hidden', visible ? 'true' : 'false');
      if (!visible) renderArtwork();
    };

    if (document.body.classList.contains('asif-radio-product')) {
      setPrimaryVisible(true);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      let measureFrame = 0;
      const measure = () => {
        const rect = primary.getBoundingClientRect();
        const visibleHeight = Math.max(0, Math.min(window.innerHeight, rect.bottom) - Math.max(0, rect.top));
        setPrimaryVisible(visibleHeight / Math.max(1, rect.height) > 0.08);
      };
      const scheduleMeasure = () => {
        if (!('requestAnimationFrame' in window)) {
          measure();
          return;
        }
        window.cancelAnimationFrame(measureFrame);
        measureFrame = window.requestAnimationFrame(measure);
      };
      window.addEventListener('scroll', scheduleMeasure, { passive: true });
      window.addEventListener('resize', scheduleMeasure, { passive: true });
      primaryFallbackCleanup = () => {
        if ('cancelAnimationFrame' in window) window.cancelAnimationFrame(measureFrame);
        window.removeEventListener('scroll', scheduleMeasure);
        window.removeEventListener('resize', scheduleMeasure);
      };
      measure();
      return;
    }

    primaryObserver = new IntersectionObserver((entries) => {
      if (!primary.isConnected) return; // weicher Seitentausch: alte Buehne ignorieren
      const visible = entries[0]?.isIntersecting === true;
      setPrimaryVisible(visible);
    }, { threshold: 0.18 });
    primaryObserver.observe(primary);
  };

  const releasePrimaryVisibility = () => {
    primaryObserver?.disconnect();
    primaryObserver = null;
    primaryFallbackCleanup?.();
    primaryFallbackCleanup = null;
    host.classList.remove('is-primary-visible');
    host.removeAttribute('inert');
    host.setAttribute('aria-hidden', 'false');
  };

  const pauseBackgroundWork = () => {
    abortDataRequests();
    window.clearTimeout(nowPlayingTimer);
    window.clearTimeout(scheduleTimer);
    window.clearTimeout(historyTimer);
  };

  const suspendForPageLifecycle = () => {
    const shouldOfferResume = state.wantsToPlay || readPersistedIntent();
    operation += 1;
    clearRetry();
    clearBufferingWatchdog();
    stopPlaybackSegment();
    abortDataRequests();
    window.clearTimeout(nowPlayingTimer);
    window.clearTimeout(scheduleTimer);
    window.clearTimeout(historyTimer);
    if (hasSource) releaseSource();
    if (playbackReported) {
      publishAnalytics('paused', { playedSeconds: playedSeconds(), reason: 'page-lifecycle' }, 'paused');
    }
    playbackStartedAt = 0;
    playbackAccumulatedMs = 0;
    playbackReported = false;
    activeClaim = null;
    if (shouldOfferResume) {
      state.wantsToPlay = false;
      state.needsResume = true;
      transition('paused');
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-asif-radio-toggle]');
    if (toggle) {
      event.preventDefault();
      toggleFromGesture(toggle);
      return;
    }
    const copyButton = event.target.closest('[data-asif-radio-copy-stream]');
    if (copyButton) {
      event.preventDefault();
      copyButton.focus({ preventScroll: true });
      const feedback = copyButton.querySelector('[data-asif-radio-copy-label]');
      const original = feedback?.textContent || 'Streamadresse kopieren';
      const finish = (label) => {
        if (feedback) feedback.textContent = label;
        window.setTimeout(() => {
          if (feedback) feedback.textContent = original;
        }, 1800);
      };
      copyStreamAddress().then((copied) => {
        finish(copied ? 'Streamadresse kopiert' : 'Kopieren nicht möglich');
      });
      return;
    }
    const favoriteButton = event.target.closest('[data-asif-radio-favorite]');
    if (favoriteButton) {
      event.preventDefault();
      const trackId = favoriteButton.dataset.asifRadioTrackId;
      const historyItem = trackId ? state.history.find((item) => item.track?.id === trackId) : null;
      const savedItem = trackId ? state.favorites.find((item) => item.id === trackId) : null;
      const track = historyItem?.track
        ? { ...historyItem.track, artworkUrl: historyItem.artworkUrl }
        : savedItem || (trackId === state.trackId ? currentTrack() : null);
      toggleFavorite(track);
      return;
    }
    const shareButton = event.target.closest('[data-asif-radio-share]');
    if (shareButton) {
      event.preventDefault();
      const feedback = shareButton.querySelector('[data-asif-radio-share-label]');
      const original = feedback?.textContent || 'Teilen';
      shareRadio().then((result) => {
        if (result === 'cancelled') return;
        if (feedback) feedback.textContent = result === 'shared' ? 'Geteilt' : result === 'copied' ? 'Link kopiert' : 'Nicht möglich';
        window.setTimeout(() => { if (feedback) feedback.textContent = original; }, 1800);
      });
      return;
    }
    const airPlayButton = event.target.closest('[data-asif-radio-airplay]');
    if (airPlayButton) {
      event.preventDefault();
      try { audio.webkitShowPlaybackTargetPicker?.(); } catch (error) {}
    }
  });

  document.addEventListener('error', (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || !image.matches('[data-asif-radio-art]')) return;
    if (!fallbackArtworkURL) return;
    const currentArtworkURL = normalizeArtworkURL(state.artworkUrl);
    if (image.src !== currentArtworkURL) {
      renderArtwork();
      return;
    }
    if (image.src === fallbackArtworkURL) return;
    state.artworkUrl = fallbackArtworkURL;
    renderArtwork();
    syncMediaSession();
    emit();
  }, true);

  audio.addEventListener('loadstart', () => {
    if (state.wantsToPlay && hasSource && !retryTimer && !interrupted && state.status !== 'playing') {
      transition(state.retryCount ? 'retrying' : 'connecting');
    }
  });
  audio.addEventListener('timeupdate', () => {
    if (!state.wantsToPlay || interrupted || !hasSource || !isAdvancing()) return;
    if (needsLiveRejoin) { scheduleRetry('live-rejoin', true); return; }
    if (startRequestedAt && audioStartMs === null) audioStartMs = Date.now() - startRequestedAt;
    if (state.status === 'buffering') {
      clearBufferingWatchdog();
      transition('playing', { errorCode: '' });
      startPlaybackSegment();
      startStallWatch();
    }
  });
  audio.addEventListener('playing', () => {
    if (!state.wantsToPlay || !hasSource || externalMediaOwner || interrupted) { audio.pause(); return; }
    if (audio.paused || audio.readyState < 3) return;
    if (needsLiveRejoin) { scheduleRetry('live-rejoin', true); return; }
    sourceHasPlayed = true;
    const completedRetries = state.retryCount;
    hasPlayedOnce = true;
    stablePlaybackSince = Date.now();
    unsupportedCount = 0;
    clearRetry({ resetCount: true });
    clearBufferingWatchdog();
    startStallWatch();
    transition('playing', { errorCode: '', needsResume: false });
    if (!playbackReported && playbackSession > 0) {
      playbackReported = publishAnalytics('play_started', {
        retries: completedRetries
      }, 'play-started');
    }
    startPlaybackSegment();
    broadcastClaim();
    scheduleDataTimers();
  });
  // An unexplained native pause can be a headset disconnect, another app or a
  // system control. Never fight it with blind play() calls. Network failures
  // retain intent; AudioSession interruptions resume only after the OS releases it.
  audio.addEventListener('pause', () => {
    if (!state.wantsToPlay || !hasSource || !audio.paused) return;
    if (['connecting', 'retrying', 'offline', 'error'].includes(state.status)) return;
    if (interrupted || navigator.audioSession?.state === 'interrupted') {
      interrupted = true;
      clearRetry();
      clearBufferingWatchdog();
      clearStallWatch();
      stopPlaybackSegment();
      transition('paused', { needsResume: true });
    } else if (audio.error || audio.readyState < 3 || state.status === 'buffering') {
      scheduleRetry('network-pause');
    } else {
      pause({ preserveResume: true, reason: 'system-pause' });
    }
  });
  audio.addEventListener('waiting', () => {
    if (!state.wantsToPlay || !hasSource || interrupted) return;
    if (sourceHasPlayed) needsLiveRejoin = true;
    stopPlaybackSegment();
    transition('buffering');
    startBufferingWatchdog();
  });
  audio.addEventListener('stalled', () => {
    if (!state.wantsToPlay || !hasSource || interrupted) return;
    // Download stalled does not mean playback stopped. Preserve buffered music.
    if (!audio.paused && (bufferAhead() > 0.5 || isAdvancing())) return;
    if (sourceHasPlayed) needsLiveRejoin = true;
    stopPlaybackSegment();
    transition('buffering');
    startBufferingWatchdog();
  });
  audio.addEventListener('ended', () => {
    if (state.wantsToPlay && hasSource && audio.ended) scheduleRetry('ended');
  });
  audio.addEventListener('abort', () => {
    // Source replacement queues abort events from the previous load. Only the
    // current media state can establish that this source was actually aborted.
    if (state.wantsToPlay && hasSource && audio.error?.code === 1) scheduleRetry('abort');
  });
  audio.addEventListener('error', () => {
    if (!state.wantsToPlay || !hasSource || !audio.error) return;
    const code = audio.error.code;
    // Code 4 heisst nach einer Unterbrechung oft nur "offline", nicht "kann kein HLS".
    if (transport.startsWith('hls') && (code === 3 || (code === 4 && !hasPlayedOnce))) hlsFailures = 2;
    scheduleRetry(`stream-error-${code}`);
  });
  const handleAirPlayAvailability = (event) => {
    state.airPlayAvailable = event.availability === 'available' && typeof audio.webkitShowPlaybackTargetPicker === 'function';
    render();
  };
  const bindAirPlayAvailability = () => {
    if (airPlayAvailabilityListening || typeof audio.webkitShowPlaybackTargetPicker !== 'function') return;
    audio.addEventListener('webkitplaybacktargetavailabilitychanged', handleAirPlayAvailability);
    airPlayAvailabilityListening = true;
  };
  const unbindAirPlayAvailability = () => {
    if (!airPlayAvailabilityListening) return;
    audio.removeEventListener('webkitplaybacktargetavailabilitychanged', handleAirPlayAvailability);
    airPlayAvailabilityListening = false;
    state.airPlayAvailable = false;
  };
  audio.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', () => {
    state.airPlayActive = audio.webkitCurrentPlaybackTargetIsWireless === true;
    render();
  });

  const recoverPlayback = (reason) => {
    if (!state.wantsToPlay || externalMediaOwner || interrupted) return;
    if (reason === 'interruption-ended' || needsLiveRejoin) {
      clearRetry();
      scheduleRetry('live-rejoin', true);
      return;
    }
    // Foreground/network hints must not restart a healthy pending load. Its
    // existing deadline still bounds a hung promise, including after suspension.
    if (hasSource && !audio.paused && !audio.error && bufferingTimer && Date.now() - bufferingStartedAt < config.bufferingTimeout) return;
    if (hasSource && !audio.paused && !audio.ended && !audio.error) {
      if (isAdvancing() || (state.status === 'playing' && Date.now() - lastProgressAt < config.stallTimeout)) {
        if (state.status !== 'playing') {
          clearRetry();
          clearBufferingWatchdog();
          transition('playing', { needsResume: false, errorCode: '' });
          startPlaybackSegment();
          startStallWatch();
        }
        return;
      }
    }
    clearRetry();
    scheduleRetry(reason, true);
  };
  window.addEventListener('offline', () => {
    if (!state.wantsToPlay || interrupted) return;
    // onLine is a hint. Drain the native buffer and probe with capped backoff
    // even if a device never emits online after a Wi-Fi/mobile handover.
    if (hasSource && !audio.paused && (bufferAhead() > 0.5 || isAdvancing())) return;
    scheduleRetry('offline');
  });
  window.addEventListener('online', () => {
    refreshNowPlaying({ force: true });
    recoverPlayback('online');
  });
  navigator.connection?.addEventListener?.('change', () => recoverPlayback('network-change'));
  navigator.audioSession?.addEventListener?.('statechange', () => {
    if (navigator.audioSession.state === 'interrupted') {
      interrupted = true;
      clearRetry();
      clearBufferingWatchdog();
      clearStallWatch();
      stopPlaybackSegment();
      if (state.wantsToPlay) transition('paused', { needsResume: true });
    } else if (interrupted) {
      interrupted = false;
      recoverPlayback('interruption-ended');
    }
  });
  window.addEventListener('pageshow', (event) => {
    documentSuspended = false;
    bindAirPlayAvailability();
    bindPrimaryVisibility();
    if (event.persisted) {
      if (state.wantsToPlay) recoverPlayback('pageshow');
      else if (readPersistedIntent()) {
        state.wantsToPlay = false;
        state.needsResume = true;
        transition('paused');
        window.setTimeout(() => { try { attemptResume(); } catch (error) {} }, 0);
      }
      refreshNowPlaying({ force: true });
      if (scheduleRequested) refreshSchedule({ force: true });
      if (historyRequested) refreshHistory({ force: true });
    }
    scheduleDataTimers();
  });
  // pagehide feuert auf iOS auch, wenn eine installierte App in den Hintergrund
  // geht. Frueher starb hier der Ton. Jetzt werden nur Zeitgeber und Anfragen
  // stillgelegt, die Wiedergabe laeuft weiter.
  window.addEventListener('pagehide', (event) => {
    unbindAirPlayAvailability();
    // Audit 24.09.: Laeuft das Radio weiter (App im Hintergrund), bleiben die
    // Titelabfragen aktiv, sonst friert der Sperrbildschirm beim alten Titel ein.
    if (!event.persisted && state.wantsToPlay) return;
    documentSuspended = true;
    if (event.persisted || state.wantsToPlay) {
      pauseBackgroundWork();
      return;
    }
    suspendForPageLifecycle();
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      bindAirPlayAvailability();
      documentSuspended = false;
      recoverPlayback('resume-visible');
      if (domDirty) { domDirty = false; render(); }
      refreshNowPlaying({ force: true });
    } else {
      unbindAirPlayAvailability();
    }
    scheduleDataTimers();
  });
  document.addEventListener('shopify:section:load', bindPrimaryVisibility);
  document.addEventListener('shopify:section:unload', (event) => {
    const primary = document.querySelector('[data-asif-radio-primary]');
    if (!primary || (event.target instanceof Element && event.target.contains(primary))) {
      releasePrimaryVisibility();
    }
  });

  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel(channelName);
    channel.addEventListener('message', (event) => {
      if (!event.data || event.data.source === instanceId) return;
      if (event.data.type === 'claim' && state.wantsToPlay) {
        const remoteClaim = event.data.claim;
        if (!remoteClaim || !activeClaim || compareClaims(remoteClaim, activeClaim) > 0) {
          pause({ notify: false, preserveResume: true, reason: 'cross-tab' });
        } else {
          broadcastClaim();
        }
      }
    });
  }

  const bindLiveMediaSession = () => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler('play', () => {
        beginPlay({ userGesture: true, surface: 'media-session' }).catch(() => {});
      });
      navigator.mediaSession.setActionHandler('pause', () => pause({ surface: 'media-session' }));
      navigator.mediaSession.setActionHandler('stop', () => pause({ surface: 'media-session' }));
      ['seekbackward', 'seekforward', 'seekto', 'previoustrack', 'nexttrack'].forEach((action) => {
        try { navigator.mediaSession.setActionHandler(action, null); } catch (error) {}
      });
    } catch (error) {}
  };
  bindLiveMediaSession();
  window.addEventListener('asif:audio-owner', (event) => {
    const owner = event.detail?.owner;
    if (!['soundcloud', 'spotify'].includes(owner)) return;
    externalMediaOwner = true;
    pause({ surface: owner, reason: owner });
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
      ['play', 'pause', 'stop', 'seekbackward', 'seekforward', 'seekto', 'previoustrack', 'nexttrack'].forEach((action) => {
        try { navigator.mediaSession.setActionHandler(action, null); } catch (error) {}
      });
    } catch (error) {}
  });

  state.favorites = readFavorites();
  syncCurrentFavorite();
  bindAirPlayAvailability();
  const persistedIntent = readPersistedIntent();
  if (persistedIntent) {
    state.needsResume = true;
    hasEverEngaged = true;
    state.status = 'paused';
  }

  // One recovery path for reloads, user play and transport failure. Network
  // rejection must not erase listening intent; policy rejection needs a tap.
  const attemptResume = () => {
    if (externalMediaOwner || !readPersistedIntent() || document.hidden) return;
    if (state.wantsToPlay && hasSource) return;
    activeSurface = 'resume';
    beginPlay({ notify: false, surface: 'resume' });
  };

  const setQuality = (value) => {
    if (!['auto', 'low', 'high'].includes(value) || value === quality) return;
    quality = value;
    state.quality = value;
    try { localStorage.setItem(qualityKey, value); } catch {}
    // A preference change never starts paused audio or steals a provider's session.
    if (state.wantsToPlay && !externalMediaOwner && !interrupted) {
      needsLiveRejoin = true;
      hlsFailures = 0;
      clearRetry();
      scheduleRetry('quality-change', true);
    }
    render();
  };
  window.addEventListener('asif:audio-stop', () => pause({ reason: 'sleep-timer' }));

  window.AsIfRadio = Object.freeze({
    version: '2.4.0',
    play: () => beginPlay({ userGesture: true, surface: 'api' }),
    pause,
    stop: pause,
    toggle: () => toggleFromGesture(null),
    setQuality,
    getEffectiveQuality: () => effectiveLevel,
    getDiagnostics: () => ({ version: '2.4.0', status: state.status, quality, transport, audioStartMs, bufferSeconds: Math.round(bufferAhead()), retries: state.retryCount, error: state.errorCode || '' }),
    ensureData,
    refreshNowPlaying: () => refreshNowPlaying({ force: true }),
    refreshSchedule: () => refreshSchedule({ force: true }),
    refreshHistory: () => refreshHistory({ force: true }),
    toggleFavorite,
    share: shareRadio,
    showAirPlayPicker: () => audio.webkitShowPlaybackTargetPicker?.(),
    getState: snapshot,
    // Billiger Lesezugriff ohne tiefe Kopie: asif-spa.js fragt das bei jeder
    // Zeigerbewegung ueber einen Link ab.
    isListening: () => state.wantsToPlay,
    status: () => state.status
  });

  bindPrimaryVisibility();
  render();
  refreshNowPlaying();
  scheduleDataTimers();
  window.dispatchEvent(new CustomEvent('asif:radio-ready', { detail: snapshot() }));
  attemptResume();

  // SPA-Vertrag (asif-spa.js): nach einem Seitentausch haengen neue
  // Bedienelemente im Dokument. Zustand neu ausgeben, Sichtbarkeit des
  // grossen Players neu anbinden. Das Audio-Element selbst bleibt.
  document.addEventListener('asif:page', (event) => {
    if (event.detail?.initial) return;
    bindPrimaryVisibility();
    render();
    renderArtwork();
  });
})();
